import { ANALYTICS_OFF } from '../analytics/analytics_off.js';
import type { AppsFlyerConsentDecision } from '../privacy/applovin_consent_policy.js';

function getP(): any {
  // No AppsFlyer for JustSmash: every call below sees no plugin and does nothing.
  if (ANALYTICS_OFF) return null;
  const plugins = (window as any)?.Capacitor?.Plugins ?? null;
  if (!plugins) return null;
  return plugins.AppsFlyer ?? plugins.AppsFlyerPlugin ?? null;
}

type AppsFlyerEventValue = string | number | boolean;

let commonEventValues: Record<string, AppsFlyerEventValue> = {
  campaign: 'unknown',
};

type AppsFlyerState = 'waiting_for_consent' | 'starting' | 'started' | 'blocked';
type PendingOperation = () => Promise<void>;

const MAX_PENDING_OPERATIONS = 100;
let state: AppsFlyerState = 'waiting_for_consent';
const pendingOperations: PendingOperation[] = [];

function runOrQueue( operation: PendingOperation ): void
{
  if ( state === 'started' )
  {
    void operation();
    return;
  }
  if ( state !== 'waiting_for_consent' && state !== 'starting' ) return;

  if ( pendingOperations.length >= MAX_PENDING_OPERATIONS ) pendingOperations.shift();
  pendingOperations.push( operation );
}

function flushPendingOperations(): void
{
  const operations = pendingOperations.splice( 0 );
  for ( const operation of operations ) void operation();
}

export const AppsFlyer = {
  isAvailable(): boolean {
    return !!getP();
  },

  async applyConsentAndStart(
    consent: AppsFlyerConsentDecision,
  ): Promise<{ started: boolean; uid: string | null }> {
    const p = getP();
    if ( !p ) return { started: false, uid: null };

    state = 'starting';
    try
    {
      const result = await p.initialize( {
        startAllowed: consent.startAllowed,
      } );
      const started = result?.started === true;
      state = started ? 'started' : 'blocked';
      if ( started ) flushPendingOperations();
      else pendingOperations.length = 0;

      return {
        started,
        uid: typeof result?.uid === 'string' && result.uid.trim() ? result.uid.trim() : null,
      };
    }
    catch ( e )
    {
      state = 'blocked';
      pendingOperations.length = 0;
      console.error( '[AppsFlyer] consent-gated initialization failed:', e );
      return { started: false, uid: null };
    }
  },

  async debugKeychainInfo(): Promise<string> {
    const p = getP();
    if (!p) return 'AppsFlyer plugin not available';
    try {
      const result = await p.debugKeychainInfo();
      return result?.info ?? 'no info';
    } catch (e) {
      console.error('[AppsFlyer] debugKeychainInfo failed:', e);
      return String(e);
    }
  },

  async debugClearKeychain(): Promise<void> {
    const p = getP();
    if (!p) return;
    try {
      await p.debugClearKeychain();
    } catch (e) {
      console.error('[AppsFlyer] debugClearKeychain failed:', e);
    }
  },

  async setCustomerUserId(uid: string): Promise<void> {
    const p = getP();
    if (!p) return;
    runOrQueue( async () => {
      try {
        await p.setCustomerUserId({ uid });
      } catch (e) {
        console.error('[AppsFlyer] setCustomerUserId failed:', e);
      }
    } );
  },

  async getAppsFlyerUID(): Promise<string | null> {
    const p = getP();
    if (!p || state !== 'started') return null;
    try {
      const result = await p.getAppsFlyerUID();
      return result?.uid ?? null;
    } catch (e) {
      console.error('[AppsFlyer] getAppsFlyerUID failed:', e);
      return null;
    }
  },

  async getConversionData(): Promise<Record<string, unknown> | null> {
    const p = getP();
    if (!p?.getConversionData || state !== 'started') return null;
    try {
      const result = await p.getConversionData();
      const data = result?.data;
      return data && typeof data === 'object' ? data as Record<string, unknown> : null;
    } catch (e) {
      console.error('[AppsFlyer] getConversionData failed:', e);
      return null;
    }
  },

  setCommonEventValues(values: Record<string, AppsFlyerEventValue>): void {
    commonEventValues = { ...commonEventValues, ...values };
  },

  async logEvent(name: string, values?: Record<string, any>): Promise<void> {
    const p = getP();
    if (!p) {
      console.warn('[AppsFlyer] plugin not available');
      return;
    }
    const mergedValues = { ...commonEventValues, ...(values ?? {}) };
    runOrQueue( async () => {
      try {
        if (Object.keys(mergedValues).length > 0) {
          await p.logEvent({ name, values: mergedValues });
        } else {
          await p.logEvent({ name });
        }
      } catch (e) {
        console.error('[AppsFlyer] logEvent failed:', e);
      }
    } );
  },

  async logAdRevenue(
    revenue: number,
    currency: string,
    network: string,
    additionalParameters: Record<string, unknown>,
    mediationNetwork?: string,
  ): Promise<void> {
    const p = getP();
    if (!p?.logAdRevenue) {
      console.warn('[AppsFlyer] logAdRevenue not available');
      return;
    }
    runOrQueue( async () => {
      try {
        await p.logAdRevenue({
          revenue,
          currency,
          network,
          ...(mediationNetwork ? { mediationNetwork } : {}),
          additionalParameters: {
            ...commonEventValues,
            ...additionalParameters,
          },
        });
      } catch (e) {
        console.error('[AppsFlyer] logAdRevenue failed:', e);
      }
    } );
  }
};
